/**
 * DraftCard — point névralgique UX du système (Sprint 3).
 *
 * Principe directeur (cf. project memory + plan v3) : **si l'utilisatrice
 * met 5 min à approuver un draft, elle ne le fera plus au bout de 2
 * semaines**. Le système devient publieur sans humain → effondrement
 * qualité → Tier 4 (boucle de feedback) s'éteint. Cible mesurable :
 * approve médian < 15 s sur les 10 premières œuvres.
 *
 * Conséquences UX :
 * - **Lisible 10 s** : titre proéminent, description tronquée à 4 lignes
 *   avec "lire plus" inline (pas de modal), tags discret, hashtags
 *   monospace. Pas de scroll dans la carte.
 * - **Action 1 click** :
 *   - Approuver = bouton primary, *clic = approve immédiat*. Pas de
 *     `useConfirm` (rejet est destructif, approve ne l'est pas — l'humain
 *     peut désapprouver après).
 *   - Régénérer = bouton ghost, ouvre `<Modal>` avec textarea hint
 *     optionnel (placeholder "Plus court ? Moins lyrique ?") + submit.
 *   - Rejeter = bouton quiet danger, `useConfirm()`.
 * - 4 cartes côte à côte sur desktop, 2×2 tablette, stack mobile (CSS).
 * - Si `suggested_tags` non vide à l'approve → modal "Ajouter ces tags"
 *   gérée par le parent (cf. WorkDetailPage).
 */

import { useState } from 'react'
import { CheckCircle2, RefreshCw, RotateCcw, Trash2, XCircle } from 'lucide-react'

import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import MediaImage from '@/components/ui/MediaImage'
import Modal from '@/components/ui/Modal'
import PlatformDot from '@/components/ui/PlatformDot'
import Textarea from '@/components/ui/Textarea'
import {
  DRAFT_KIND_FR,
  DRAFT_KIND_TO_PLATFORM,
  DRAFT_STATUS_FR,
  type DraftResponse,
} from '@/types/drafts'
import type { Platform as DotPlatform } from '@/components/ui/PlatformDot'

import './DraftCard.css'

interface DraftCardProps {
  draft: DraftResponse
  onApprove: (draft: DraftResponse) => void
  onRegenerate: (draft: DraftResponse, hint: string | null) => void
  onReject: (draft: DraftResponse) => void
  /** Bascule status `approved|rejected → pending` sans appel Claude. */
  onReopen?: (draft: DraftResponse) => void
  /** Mutation en cours sur ce draft — désactive l'interaction. */
  isBusy?: boolean
}

export default function DraftCard({
  draft,
  onApprove,
  onRegenerate,
  onReject,
  onReopen,
  isBusy = false,
}: DraftCardProps) {
  const [regenOpen, setRegenOpen] = useState(false)
  const [hint, setHint] = useState('')
  const [expanded, setExpanded] = useState(false)

  const version = draft.current_version
  const platformDot: DotPlatform = DRAFT_KIND_TO_PLATFORM[draft.kind] as DotPlatform

  const submitRegenerate = () => {
    onRegenerate(draft, hint.trim() || null)
    setRegenOpen(false)
    setHint('')
  }

  // Caractères-clés pour le statut visuel : approved = neutre + ✓ encre,
  // rejected = quiet + ✕, pending = pas de badge (la carte est juste là).
  const statusBadge = (() => {
    if (draft.status === 'approved') {
      return (
        <Badge variant="success" size="sm" dot>
          {DRAFT_STATUS_FR[draft.status]}
        </Badge>
      )
    }
    if (draft.status === 'rejected') {
      return (
        <Badge variant="warning" size="sm">
          {DRAFT_STATUS_FR[draft.status]}
        </Badge>
      )
    }
    return null
  })()

  return (
    <article
      className={`draft-card${draft.status === 'rejected' ? ' draft-card-rejected' : ''}`}
      aria-busy={isBusy || undefined}
    >
      <header className="draft-card-header">
        <div className="draft-card-platform">
          <PlatformDot platform={platformDot} />
          <span className="draft-card-kind">{DRAFT_KIND_FR[draft.kind]}</span>
          {/* Refonte drafts v12 — badge dédié pour les sous-kinds Etsy.
              Disambiguïté UI immédiate dans une liste cross-œuvres où le
              wording "Annonce Etsy (physique)" peut être troncqué. */}
          {draft.kind === 'etsy_listing_physical' && (
            <Badge variant="default" size="sm" title="Listing Etsy physique (print/original)">
              Physical
            </Badge>
          )}
          {draft.kind === 'etsy_listing_digital' && (
            <Badge variant="info" size="sm" title="Listing Etsy digital (instant download)">
              Digital
            </Badge>
          )}
        </div>
        <div className="draft-card-header-right">
          {statusBadge}
          {/* Vignette canonique œuvre — uniquement sur la liste cross-œuvres
              (le champ est NULL sur la route per-œuvre où l'œuvre est connue
              par le contexte). Reconnaissance visuelle immédiate. */}
          {draft.work_thumb_minio_key && (
            <MediaImage
              minioKey={draft.work_thumb_minio_key}
              alt={draft.work_title_fr ?? 'Œuvre'}
              className="draft-card-work-thumb"
            />
          )}
        </div>
      </header>

      {/* ── Contenu version courante ────────────────────────────────────── */}
      {version ? (
        <div className="draft-card-body">
          {/*
            S8 PR2 — preview vidéo inline pour les kinds vidéo (INSTAGRAM_REEL
            + TIKTOK_POST). Si `video_id` IS NULL → render en cours (le hook
            useDrafts poll en backoff exponentiel 5s/10s/20s/30s plafond). Si
            `video_minio_key_presigned` populated → lecteur HTML5 natif.
          */}
          {(draft.kind === 'instagram_reel' || draft.kind === 'tiktok_post') && (
            <div className="draft-card-video">
              {version.video_minio_key_presigned ? (
                <video
                  controls
                  preload="metadata"
                  src={version.video_minio_key_presigned}
                  className="draft-card-video-player"
                  aria-label="Preview vidéo Ken Burns"
                />
              ) : (
                <div className="draft-card-video-pending" aria-live="polite">
                  <span className="ui-caption">Render vidéo en cours…</span>
                </div>
              )}
            </div>
          )}

          {version.title && <h3 className="draft-card-title">{version.title}</h3>}

          {version.description && (
            <p
              className={`draft-card-description${expanded ? '' : ' draft-card-description-clamped'}`}
            >
              {version.description}
            </p>
          )}

          {/* "Lire plus" inline — pas de modal pour préserver < 10s scan. */}
          {version.description && version.description.length > 280 && (
            <Button
              variant="quiet"
              size="sm"
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="draft-card-toggle"
            >
              {expanded ? 'Réduire' : 'Lire plus'}
            </Button>
          )}

          {version.tags && version.tags.length > 0 && (
            <div className="draft-card-tags">
              {version.tags.map((tag) => (
                <span key={tag} className="draft-card-tag">
                  {tag}
                </span>
              ))}
            </div>
          )}

          <footer className="draft-card-meta">
            <span className="ui-caption">
              v{version.version_number}
              {(draft.versions_count ?? 0) > 1 ? `/${draft.versions_count ?? 0}` : ''}
            </span>
            {version.cache_hit && (
              <span className="ui-caption" title="Cache prompt-caching utilisé">
                · cache
              </span>
            )}
            {(version.cost_usd_micro ?? 0) > 0 && (
              <span className="ui-caption" title="Coût estimé Anthropic">
                · ${((version.cost_usd_micro ?? 0) / 1_000_000).toFixed(4)}
              </span>
            )}
          </footer>
        </div>
      ) : (
        <div className="draft-card-body draft-card-empty">
          <p className="ui-text-muted">Pas de version générée.</p>
        </div>
      )}

      {/* ── Actions — 1 click ──────────────────────────────────────────── */}
      <footer className="draft-card-actions">
        {draft.status !== 'approved' && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => onApprove(draft)}
            disabled={isBusy || !version}
            aria-label="Approuver ce draft"
          >
            <CheckCircle2 size={14} aria-hidden="true" /> Approuver
          </Button>
        )}
        {(draft.status === 'rejected' || draft.status === 'approved') && onReopen && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onReopen(draft)}
            disabled={isBusy}
            aria-label="Réouvrir ce draft (sans appel Claude)"
            title={
              draft.status === 'rejected'
                ? 'Remettre en pending sans régénérer le texte'
                : 'Annuler l’approbation pour relancer la sélection de mockups'
            }
          >
            <RotateCcw size={14} aria-hidden="true" /> Réouvrir
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setRegenOpen(true)}
          disabled={isBusy}
          aria-label="Régénérer ce draft"
        >
          <RefreshCw size={14} aria-hidden="true" /> Régénérer
        </Button>
        {draft.status !== 'rejected' && (
          <Button
            variant="quiet"
            size="sm"
            onClick={() => onReject(draft)}
            disabled={isBusy}
            aria-label="Rejeter ce draft"
            className="draft-card-reject"
          >
            <XCircle size={14} aria-hidden="true" /> Rejeter
          </Button>
        )}
      </footer>

      {/* ── Modal régénérer (hint optionnel — Tier 4) ──────────────────── */}
      <Modal
        isOpen={regenOpen}
        onClose={() => setRegenOpen(false)}
        title="Régénérer ce draft ?"
        kicker={`${DRAFT_KIND_FR[draft.kind]} · v${(version?.version_number ?? 0) + 1}`}
        size="form"
      >
        <Modal.Body>
          <p className="ui-text-muted">
            Un hint t'aide à orienter Claude. Optionnel — laisse vide pour une régénération à
            l'identique.
          </p>
          <Textarea
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            placeholder="Plus court ? Moins lyrique ? Autre angle ?"
            rows={3}
            maxLength={1000}
            aria-label="Hint de régénération"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => setRegenOpen(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={submitRegenerate} loading={isBusy}>
            <Trash2 size={14} aria-hidden="true" /> Régénérer
          </Button>
        </Modal.Footer>
      </Modal>
    </article>
  )
}
